type BuildingLink = { to: string; label: string } | { href: string; label: string };

export const platformEmergence = [
  {
    id: "breakthrough",
    title: "Breakthrough",
    body: "A new technology or way of operating appears. The industry platform that follows is how growth expands — digital imaging enlarged by phones, cameras, computers, and advertising, belonging to none of them alone.",
  },
  {
    id: "first-generation",
    title: "First generation",
    body: "The platform exists in an early form, often unrecognized or underestimated, while the first generation spends itself on infrastructure.",
  },
  {
    id: "lag-derivatives-support",
    title: "Lag, derivatives, and support",
    body: "Derivatives try to use the platform. Support environments — other platforms that make the breakthrough reachable — increase access. The lag lasts until adoption catches the infrastructure.",
  },
  {
    id: "second-generation",
    title: "Second generation",
    body: "Widespread adoption stabilizes the platform. It then carries many approaches for decades. New offerings stand on the platforms of older ones; there is no clean start, the complexity moves.",
  },
] as const;

export const platformLayers: {
  id: string;
  step: string;
  title: string;
  book: string;
  product: string;
  links: BuildingLink[];
}[] = [
  {
    id: "business-model",
    step: "01",
    title: "Business model",
    book: "The model has to move with the platform. New platform technology with the old model is only architectural. Radical change renews capability and offering together.",
    product:
      "An operator who owns the outcome can still run the prediction. A new runtime with the old operating model is not yet a product.",
    links: [
      {
        to: "/thinking/prediction-has-to-operate",
        label: "Prediction has to operate",
      },
      { to: "/work-with-jay#platform-strategy", label: "Platform strategy" },
    ],
  },
  {
    id: "predictive-offering",
    step: "02",
    title: "Predictive offering",
    book: "An offering is co-created use, not a product you hand over once. Platforms are also where intelligent software and agents take on reasoning — the bridge from this writing to a predictive product.",
    product:
      "ecosystem.Ai scores while the person is still acting, writes the response, and updates the next score. The response is part of the offering.",
    links: [
      { to: "/work/ecosystem-ai", label: "ecosystem.Ai" },
      {
        to: "/thinking/behavioral-intelligence-architecture",
        label: "Behavioral intelligence architecture",
      },
    ],
  },
  {
    id: "service-delivery",
    step: "03",
    title: "Service delivery",
    book: "The platform a company builds is a service-delivery platform: access and use in the interaction, not ownership of an object. Business models become embedded in that platform.",
    product:
      "Score in the channel while the person is still there. There is no product release that ends the conversation — only incremental changes an operator can still run.",
    links: [
      { to: "/work/ecosystem-ai", label: "Closed loop" },
      { href: "https://developer.ecosystem.ai", label: "Operator docs" },
    ],
  },
  {
    id: "open-edge",
    step: "04",
    title: "Open edge",
    book: "Derivatives and support environments use the platform. They do not become it. Value is created on the platform and may be captured by the platform or by whoever uses it.",
    product:
      "Campaigns, channels, and generated products vary at the edge. Commonality stays in the core — configuration and composition, not a fork.",
    links: [
      {
        to: "/thinking/service-lines-and-product-lines",
        label: "Service lines and product lines",
      },
      { to: "/work/ecosystemcode", label: "EcosystemCode" },
    ],
  },
  {
    id: "stable-platform",
    step: "05",
    title: "Stable platform",
    book: "The second-generation core is the industry’s stable base after the infrastructure generation. Platforms then carry many approaches for decades.",
    product:
      "Prediction server, Client Pulse Responder, governed ontology, and generator. Platform engineering is sustaining this core so a predictive offering can be operated.",
    links: [
      { to: "/work/ecosystem-ai", label: "Prediction platform" },
      { to: "/work/ecosystemcode", label: "Executable architecture" },
      { to: "/resources/built-to-thrive", label: "Built to Thrive" },
    ],
  },
];

export const lifecycle = [
  {
    id: "prediction-story",
    step: "01",
    title: "Prediction story",
    body: "The work starts with the interaction, not the repository. Who is acting, what has to be decided while they are still there, and what evidence the next score is allowed to use. That story names the project, the model class, the channel, and the response that updates the following interaction. Notebooks and prediction-story templates hold it before a runtime does. The workbench is where an operator configures it.",
    links: [
      { to: "/work/ecosystem-ai", label: "ecosystem.Ai" },
      {
        href: "https://github.com/ecogenetic/ecosystem-algorithms",
        label: "Prediction-story notebooks",
      },
    ],
  },
  {
    id: "spec-driven-ai",
    step: "02",
    title: "Spec-driven AI",
    body: "The story becomes a specification a generator can refuse. Class, sequence, state, activity, package, and component diagrams are checked against a living OWL/RDF ontology and SHACL shapes. Missing entities, undefined states, duplicate names, and orphaned relationships fail before code exists. The default path is a deterministic template. AI is on only where you turn it on. A prompt is not a substitute for the language of the domain.",
    links: [
      { to: "/work/ecosystemcode", label: "EcosystemCode" },
      {
        to: "/thinking/ontology-and-hallucination",
        label: "Ontology as a binding",
      },
      { href: "https://ontology.ecosystem.ai", label: "Live catalog" },
    ],
  },
  {
    id: "agentic-processes",
    step: "03",
    title: "Agentic processes",
    body: "Agents move the spec. They do not become the system of record. Data agents explain campaign and runtime data the platform already stored. Modeling agents draft diagrams and ontology updates that still have to pass the pre-generation checks. Coding agents implement against that contract. Cursor, Hermes, OpenClaw, and Devin are the instruments I use for the coding work. Each handoff is a process: review, fact injection, and roles taken from the ontology.",
    links: [
      { to: "/work/ecosystem-ai", label: "Data agents on the platform" },
      { to: "/work/ecosystemcode", label: "Checks before generation" },
    ],
  },
  {
    id: "coding",
    step: "04",
    title: "Coding",
    body: "What leaves the spec is a stack you can run and audit. A generated baseline is a React UI, a Node API with a repository pattern, MongoDB, authorization from ontology roles, OpenAPI, an MCP server, Docker, and synthetic data aligned to the class model. The trace is diagram, code, and ontology in one line. The prediction platform is the other stack on the same discipline: a Java Client Pulse Responder, Python workers and notebooks, and a CLI. I review the change against the spec. The agent does not merge itself.",
    links: [
      { to: "/work/open-source", label: "Public engineering" },
      { to: "/work/ecosystemcode", label: "The generated baseline" },
    ],
  },
  {
    id: "deployment",
    step: "05",
    title: "Deployment",
    body: "A baseline that cannot be installed is unfinished. The same system deploys locally, on AWS, on Azure, and on GCP. Docker and Kubernetes are the two ways it travels: Compose, images, and keys for a local or single-host install; Kubernetes when the runtime, workers, and workbench have to be scheduled as a cluster. The CLI, start scripts, and the prediction server stay the control plane — projects, deployments, workers, and domain-catalog sync. Handoff is a running system on the target you chose. The public install path sits with the rest of the open engineering.",
    links: [
      { href: "https://github.com/ecogenetic/ecosystem", label: "Local install" },
      { href: "https://github.com/ecogenetic/ecosystem-cli", label: "CLI" },
      { href: "https://ecosystemcode.com", label: "ecosystemcode.com" },
    ],
  },
  {
    id: "production-reliability",
    step: "06",
    title: "In production and reliability",
    body: "Production is the closed loop. The Client Pulse Responder scores while the person is still acting and records the response, so the next score is not a guess. Reliability is the spec remaining true after deploy: the ontology still binds roles and language, the runtime still answers, and a change still traces to the model. Data agents report what was stored. They do not invent a metric. Someone who owns the outcome has to be able to operate it. A demo that cannot be operated is not the product.",
    links: [
      {
        href: "https://github.com/ecogenetic/ecosystem-runtime-localbuild",
        label: "Public runtime",
      },
      { href: "https://developer.ecosystem.ai", label: "Operator docs" },
      {
        to: "/thinking/prediction-has-to-operate",
        label: "Prediction has to operate",
      },
    ],
  },
] as const;
